import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import CheckIcon from '@mui/icons-material/Check';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TemConnectingStepStatus, TemConnectionModalBodyProps } from './temConnectionModalBody.model';
import { TemConnectionBodySteps } from './temConnectionModalBody.model';
import styles from './temConnectionModalBody.module.scss';

export function TemConnectionModalBody({ step, connectingStepStatus }: TemConnectionModalBodyProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);

  const getConnectingDisplayedContent = useCallback(
    (status?: TemConnectingStepStatus) =>
      status ? (
        <>
          <div className={styles.temConnectionInfoRow}>
            {status.helpdeskSession === TemConnectionBodySteps.Success && <CheckIcon />}
            <CoreTypography variant={CoreTypographyVariant.Body01Regular}>
              {toString(t('remoteDesktopModal.steps.helpdesk', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))}
            </CoreTypography>
          </div>
          <div className={styles.temConnectionInfoRow}>
            {status.waitingForTerminal === TemConnectionBodySteps.Success && <CheckIcon />}
            <CoreTypography variant={CoreTypographyVariant.Body01Regular}>
              {toString(t('remoteDesktopModal.steps.connect', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))}
            </CoreTypography>
          </div>
          <div className={styles.temConnectionInfoRow}>
            {status.redirectingToDesktop === TemConnectionBodySteps.Success && <CheckIcon />}
            <CoreTypography variant={CoreTypographyVariant.Body01Regular}>
              {toString(t('remoteDesktopModal.steps.redirect', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))}
            </CoreTypography>
          </div>
        </>
      ) : (
        <CoreTypography customClassName={styles.connectingSubtitle} variant={CoreTypographyVariant.Body01Regular}>
          {toString(t('remoteDesktopModal.steps.connecting', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))}
        </CoreTypography>
      ),
    [t],
  );

  const getBannerMessage = useCallback(
    (connectionStep: TemConnectionBodySteps) => {
      switch (connectionStep) {
        case TemConnectionBodySteps.NotAllowed: {
          return t('remoteDesktopModal.steps.notAllowed', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
        }
        case TemConnectionBodySteps.Failure: {
          return t('remoteDesktopModal.steps.failure', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
        }
        case TemConnectionBodySteps.RefusedByCustomer: {
          return t('remoteDesktopModal.steps.refusedByCustomer', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
        }
        default: {
          return t('remoteDesktopModal.steps.success', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
        }
      }
    },
    [t],
  );

  const handleDisplayedContent = useCallback(
    (connectionStep: TemConnectionBodySteps, status?: TemConnectingStepStatus) => {
      switch (connectionStep) {
        case TemConnectionBodySteps.Success:
        case TemConnectionBodySteps.Failure:
        case TemConnectionBodySteps.RefusedByCustomer:
        case TemConnectionBodySteps.NotAllowed: {
          return (
            <div className={styles.bannerContainer}>
              <Banner
                message={getBannerMessage(connectionStep)}
                severity={connectionStep === TemConnectionBodySteps.Success ? 'Success' : 'Error'}
              />
            </div>
          );
        }
        default: {
          return getConnectingDisplayedContent(status);
        }
      }
    },
    [getConnectingDisplayedContent, getBannerMessage],
  );

  const getContentByStep = useCallback(
    (connectionStep: TemConnectionBodySteps, status?: TemConnectingStepStatus) => (
      <div className={styles.temConnectionContent}>
        <CoreTypography customClassName="content-title" variant={CoreTypographyVariant.Title02Bold}>
          {toString(t('remoteDesktopModal.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))}
        </CoreTypography>
        {handleDisplayedContent(connectionStep, status)}
      </div>
    ),
    [t, handleDisplayedContent],
  );

  return getContentByStep(step, connectingStepStatus);
}
